import React, { useCallback, useEffect, useRef, useState } from "react";
import { Maximize, Minimize, RotateCw } from "lucide-react";
import { Button } from "./components/ui/button";
import { Skeleton } from "./components/ui/skeleton";
import { Tooltip } from "./components/ui/tooltip";

const POWER_BI_URL =
  "https://app.powerbi.com/view?r=eyJrIjoiYmM5N2FhZDctYjg1OS00ZmIwLWFmYWUtYWZiOTI1ZTMzOWI3IiwidCI6IjM4ZGM1NTc1LTI1MGYtNDM3Mi04Nzc2LTEzMzk0ZjQ5NGEyZSJ9";

const PowerBiAnalisisScreen = () => {
  const [loading, setLoading] = useState(true);
  const [isFs, setIsFs] = useState(false);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  const enterFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    else if (document.msExitFullscreen) document.msExitFullscreen();
  }, []);

  useEffect(() => {
    const onChange = () => {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      setIsFs(!!fsEl);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    document.addEventListener("msfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      document.removeEventListener("msfullscreenchange", onChange);
    };
  }, []);

  const handleReload = () => {
    if (!iframeRef.current) return;
    iframeRef.current.src = POWER_BI_URL;
    setLoading(true);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 m-0">Análisis de Calidad — Power BI</h3>
        <div className="flex items-center gap-2">
          <Tooltip title="Refrescar iframe">
            <Button variant="outline" onClick={handleReload}>
              <RotateCw className="h-4 w-4" /> Refrescar
            </Button>
          </Tooltip>
          {isFs ? (
            <Tooltip title="Salir de pantalla completa">
              <Button variant="outline" onClick={exitFullscreen}>
                <Minimize className="h-4 w-4" /> Salir FS
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Ver en pantalla completa">
              <Button onClick={enterFullscreen}>
                <Maximize className="h-4 w-4" /> Pantalla completa
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl overflow-hidden">
        <div
          ref={containerRef}
          className="relative w-full rounded-lg overflow-hidden"
          style={{ paddingTop: "56.25%", background: "#00000008" }}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center p-6 bg-white/65 backdrop-blur-sm z-[2]">
              <div className="w-full max-w-4xl space-y-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-5/6" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-4/5" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-8 w-full" />
              </div>
            </div>
          )}

          <iframe
            ref={iframeRef}
            title="DashboardAnalisisCalidad"
            src={POWER_BI_URL}
            frameBorder="0"
            allowFullScreen
            onLoad={() => setLoading(false)}
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
      </div>
    </div>
  );
};

export default PowerBiAnalisisScreen;
